// Tenantra Inc. - Tenants Management Page (Microsoft 365 Admin Style)
import { useEffect, useState } from 'react';
import { 
  Users, Plus, Search, RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { useUrlParam } from '@/services/useUrlParam';
import SubscriptionGate from '@/components/SubscriptionGate';
import { TableSkeleton, SortableHeader, useSort, exportCsv, ExportButton, RowOpenButton } from '@/components/dashboard/TableKit';
import { TenantPanel } from '@/components/dashboard/TenantPanel';

// Status config
const STATUS_CONFIG = {
  active: { label: 'Active', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  pending_consent: { label: 'Pending', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  ended: { label: 'Ended', dotColor: 'bg-slate-400', textColor: 'text-slate-500' }
};

// Empty State
function EmptyState({ filtered }) {
  if (filtered) return <div className="text-center py-12 text-sm text-slate-500" data-testid="tenants-no-match">No tenants match this filter.</div>;
  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
        <Users className="w-6 h-6 text-slate-400" />
      </div>
      <h3 className="text-base font-medium text-slate-900 mb-1">No tenants yet</h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto mb-4">
        Add your first tenancy to start tracking tenant behaviour.
      </p>
      <a href="/landlord/tenants/new" className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1">
        <Plus className="w-4 h-4" />
        Add tenancy
      </a>
    </div>
  );
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useUrlParam('status');
  const [propertyFilter, setPropertyFilter] = useUrlParam('property');
  const [leaseFilter, setLeaseFilter] = useUrlParam('lease');
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [openTenantId, setOpenTenantId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setSubscriptionRequired(false);
      const [tenantsRes, tenanciesRes] = await Promise.all([
        api.get('/tenants'),
        api.get('/tenancies', { params: { limit: 500 } })
      ]);
      setTenants(tenantsRes.data.items || []);
      setTenancies(tenanciesRes.data.items || []);
    } catch (err) {
      // Handle 402 Payment Required - show subscription gate
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
        setSubscriptionMessage(err.response?.data?.detail?.message || 'An active subscription is required to view your tenants.');
        return;
      }
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  // Get tenant's active tenancy status
  const getTenantStatus = (tenantId) => {
    const tenantTenancies = tenancies.filter(t => t.tenant_id === tenantId);
    if (tenantTenancies.some(t => t.status === 'active')) return 'active';
    if (tenantTenancies.some(t => t.status === 'pending_consent')) return 'pending_consent';
    if (tenantTenancies.some(t => t.status === 'ended')) return 'ended';
    return null;
  };

  // Property filter options (unique properties across this landlord's tenancies)
  const propertyOptions = Array.from(
    new Map(tenancies.filter(t => t.property_id).map(t => [t.property_id, t.property_address])).entries()
  ).map(([id, address]) => ({ id, address })).sort((a, b) => (a.address || '').localeCompare(b.address || ''));

  // Soonest UPCOMING active lease end for a tenant (ISO string) or null
  const nextLeaseEnd = (tenantId) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const upcoming = tenancies
      .filter(tc => tc.tenant_id === tenantId && tc.status === 'active' && tc.lease_end_date && new Date(tc.lease_end_date + 'T00:00:00') >= today)
      .map(tc => tc.lease_end_date).sort();
    return upcoming[0] || null;
  };
  const endsWithin90 = (iso) => {
    if (!iso) return false;
    const end = new Date(iso + 'T00:00:00');
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const limit = new Date(now); limit.setDate(limit.getDate() + 90);
    return end >= now && end <= limit;
  };
  const leaseEndingSoon = (tenantId) => endsWithin90(nextLeaseEnd(tenantId));

  // Filter tenants
  const filteredTenants = tenants.filter(t => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesName = t.full_name?.toLowerCase().includes(search);
      const matchesEmail = t.email?.toLowerCase().includes(search);
      const matchesId = t.tenant_unique_id?.includes(search);
      if (!matchesName && !matchesEmail && !matchesId) return false;
    }

    if (propertyFilter !== 'all') {
      if (!tenancies.some(tc => tc.tenant_id === t.id && tc.property_id === propertyFilter)) return false;
    }

    if (leaseFilter === 'ending_soon' && !leaseEndingSoon(t.id)) return false;

    if (statusFilter !== 'all') {
      const status = getTenantStatus(t.id);
      if (status !== statusFilter) return false;
    }

    return true;
  });

  const TENANT_COLUMNS = [
    { key: 'name', label: 'Name', span: 3 },
    { key: 'id', label: 'Tenant ID', span: 2 },
    { key: 'email', label: 'Email', span: 2 },
    { key: 'property', label: 'Property', span: 2 },
    { key: 'status', label: 'Status', span: 2 },
    { key: 'open', label: '', span: 1, sortable: false },
  ];
  const tenancyFor = (tenant) => tenancies.find(t => t.tenant_id === tenant.id);
  const { sorted: sortedTenants, sort, toggle: toggleSort } = useSort(filteredTenants, {
    name: (t) => t.full_name,
    id: (t) => t.tenant_unique_id,
    email: (t) => t.email,
    property: (t) => tenancyFor(t)?.property_address,
    status: (t) => (STATUS_CONFIG[getTenantStatus(t.id)] || {}).label,
  });
  const handleExport = () => exportCsv('tenantra-tenants.csv', sortedTenants, [
    { label: 'Name', value: (t) => t.full_name },
    { label: 'Tenant ID', value: (t) => t.tenant_unique_id || '' },
    { label: 'Email', value: (t) => t.email || '' },
    { label: 'Property', value: (t) => tenancyFor(t)?.property_address || '' },
    { label: 'Lease ends', value: (t) => nextLeaseEnd(t.id) || '' },
    { label: 'Status', value: (t) => (STATUS_CONFIG[getTenantStatus(t.id)] || {}).label || '' },
  ]);

  // Stats
  const stats = {
    total: tenants.length,
    active: tenancies.filter(t => t.status === 'active').length,
    goodStanding: tenants.filter(t => t.score_category === 'excellent' || t.score_category === 'good').length,
    past: tenancies.filter(t => t.status === 'ended').length
  };

  const handleRowClick = (tenant) => {
    setOpenTenantId(tenant.id);
  };

  // Show subscription gate if subscription is required
  if (subscriptionRequired) {
    return <SubscriptionGate message={subscriptionMessage} />;
  }

  return (
    <div className="space-y-5">
      {/* Header - Microsoft style: subtle, not bold */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-semibold text-slate-800">Tenants</h1>
          <p className="text-xs text-slate-400">Manage tenancies and view tenant records</p>
        </div>
      </div>

      {/* Stats Row - Mobile responsive grid */}
      {tenants.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-2 border-b border-slate-200">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-700">{stats.total}</span>
            <span className="text-xs text-slate-400">Total</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-emerald-600">{stats.active}</span>
            <span className="text-xs text-slate-400">Active</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-teal-600">{stats.goodStanding}</span>
            <span className="text-xs text-slate-400 truncate">Good standing</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-400">{stats.past}</span>
            <span className="text-xs text-slate-400">Past</span>
          </div>
        </div>
      )}

      {/* Action Row - Microsoft style: text links, not bulky buttons */}
      <div className="flex items-center gap-4">
        <a href="/landlord/tenants/new" className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1.5 hover:underline">
          <Plus className="w-3.5 h-3.5" />
          Add tenancy
        </a>
        <button 
          onClick={fetchData}
          className="text-xs text-slate-400 hover:text-slate-600 font-medium inline-flex items-center gap-1.5 hover:underline"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
        <ExportButton onClick={handleExport} disabled={loading || sortedTenants.length === 0} testId="tenants-export-btn" />
      </div>

      {/* Search and Filters - Compact */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 pl-9 text-sm bg-white border-slate-200 placeholder:text-slate-400"
          />
        </div>
        <div className="relative">
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="h-9 px-3 pr-7 bg-white border border-slate-200 rounded-md text-slate-600 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none cursor-pointer max-w-[220px] truncate"
            data-testid="tenants-property-filter"
          >
            <option value="all">All properties</option>
            {propertyOptions.map((p) => <option key={p.id} value={p.id}>{p.address || 'Unknown property'}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={leaseFilter}
            onChange={(e) => setLeaseFilter(e.target.value)}
            className="h-9 px-3 pr-7 bg-white border border-slate-200 rounded-md text-slate-600 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none cursor-pointer"
            data-testid="tenants-lease-filter"
          >
            <option value="all">All leases</option>
            <option value="ending_soon">Lease ending in 2-3 months</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 pr-7 bg-white border border-slate-200 rounded-md text-slate-600 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none cursor-pointer"
            data-testid="tenants-status-filter"
          >
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="pending_consent">Pending</option>
            <option value="ended">Ended</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Mobile FAB */}
      <a 
        href="/landlord/tenants/new" 
        className="sm:hidden fixed bottom-6 right-6 z-40 w-12 h-12 bg-teal-600 rounded-full shadow-lg flex items-center justify-center text-white active:scale-95"
      >
        <Plus className="w-5 h-5" />
      </a>

      {/* Table - Microsoft style */}
      {loading ? (
        <TableSkeleton rows={6} spans={[3, 2, 2, 2, 2, 1]} />
      ) : filteredTenants.length === 0 ? (
        <EmptyState filtered={tenancies.length > 0} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg">
          <SortableHeader columns={TENANT_COLUMNS} sort={sort} onSort={toggleSort} />

          {/* Table Rows */}
          <div className="divide-y divide-slate-100">
            {sortedTenants.map((tenant) => {
              const status = getTenantStatus(tenant.id);
              const statusConfig = STATUS_CONFIG[status] || {};
              const tenantTenancy = tenancies.find(t => t.tenant_id === tenant.id);
              
              return (
                <div
                  key={tenant.id}
                  onClick={() => handleRowClick(tenant)}
                  className="lens lens-row grid grid-cols-12 gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors items-center"
                  data-testid={`tenant-row-${tenant.id}`}
                >
                  {/* Name with dot */}
                  <div className="col-span-12 sm:col-span-3 flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusConfig.dotColor || 'bg-slate-300'}`} />
                    <span className="text-xs font-medium text-slate-800 truncate">{tenant.full_name}</span>
                    {tenantTenancy?.household?.length > 0 && <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 flex-shrink-0" title={tenantTenancy.household.map(h => h.full_name).join(', ')} data-testid={`tenant-household-${tenant.id}`}>+{tenantTenancy.household.length}</span>}
                    {tenantTenancy?.household?.some(h => h.id_status === 'pending') && <span className="text-[10px] text-amber-700 flex-shrink-0" data-testid={`tenant-roommate-pending-${tenant.id}`}>Roommate ID pending</span>}
                    <span className="sm:hidden text-[10px] text-slate-400 font-mono ml-auto">{tenant.tenant_unique_id || '------'}</span>
                  </div>
                  
                  {/* Tenant ID */}
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500 font-mono">{tenant.tenant_unique_id || '------'}</span>
                  </div>
                  
                  {/* Email */}
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500 truncate block">{tenant.email || '—'}</span>
                  </div>

                  {/* Property */}
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500 truncate block">{tenantTenancy?.property_address || '—'}</span>
                    {leaseEndingSoon(tenant.id) && <span className="text-[10px] text-amber-700" data-testid={`tenant-lease-ending-${tenant.id}`}>Lease ends {nextLeaseEnd(tenant.id)}</span>}
                  </div>
                  
                  {/* Status */}
                  <div className="hidden sm:block col-span-2">
                    <span className={`text-xs ${statusConfig.textColor || 'text-slate-500'}`}>
                      {statusConfig.label || 'None'}
                    </span>
                  </div>

                  <div className="hidden sm:flex col-span-1 justify-end">
                    <RowOpenButton onClick={() => handleRowClick(tenant)} testId={`tenant-open-${tenant.id}`} label={`Open ${tenant.full_name}`} />
                  </div>

                  {/* Mobile bottom */}
                  <div className="sm:hidden col-span-12 flex items-center justify-between -mt-0.5">
                    <span className="text-[10px] text-slate-400 truncate">{tenant.email || '—'}</span>
                    <span className={`text-[10px] ${statusConfig.textColor || 'text-slate-500'}`}>{statusConfig.label || 'None'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <TenantPanel tenantId={openTenantId} onClose={() => setOpenTenantId(null)} />
    </div>
  );
}
